import { useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Btn, Empty } from "../UI";
import { RecordSheetTable } from "./RecordSheetTable";
import { printPrescriptions, printRxList, layoutLabel } from "../../lib/rxPrint";
import { RxPrintDialog } from "./RxPrintDialog";
import { linePharmacy, lineLabel, pharmacyAddress } from "./rxShared";
import { Plus, FolderOpen, Copy, Printer, ListChecks, Store, Phone, X } from "lucide-react";

const dateOf = (rx) => rx.written_date || (rx.signed_at || rx.created_at || "").slice(0, 10);
const printable = ({ rx, l }) => !["draft", "signing", "voided"].includes(rx.status) && !l.discontinued;
const duplicable = ({ rx, l }) => rx.status !== "voided" && !l.discontinued;

const clientBlock = (c) => ({ name: `${c.first_name || ""} ${c.last_name || ""}`.trim(), dob: c.dob || "", mrn: c.mrn || "", phone: c.phone || "",
  address: [c.address, c.city, [c.state, c.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ") });

function PharmacyStrip({ row }) {
  const ph = row && linePharmacy(row.rx, row.l);
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] px-4 py-2.5 text-xs text-[#5D4037]" data-testid="rx-sheet-pharmacy">
      <span className="flex items-center gap-1.5 font-bold uppercase text-[10px] text-[#7B6157]"><Store size={12} /> Pharmacy</span>
      {!row ? <span className="text-[#84675B]">Click a row to see its pharmacy.</span> : ph
        ? <><span><strong>{ph.name}</strong>{pharmacyAddress(ph) ? `, ${pharmacyAddress(ph)}` : ""}</span><span className="flex items-center gap-1" data-testid="rx-sheet-pharmacy-phone"><Phone size={11} /> {ph.phone || "No phone on file"}</span></>
        : <span className="text-[#84675B]">No pharmacy linked for {lineLabel(row.l)}.</span>}
    </div>
  );
}

function DuplicateConfirm({ rows, busy, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[#3E2723]/40 p-4 backdrop-blur-sm" data-testid="rx-duplicate-confirm">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-3 flex items-start justify-between"><h3 className="font-heading text-lg font-bold text-[#3E2723]">Duplicate {rows.length} medication{rows.length === 1 ? "" : "s"}?</h3>
          <button type="button" onClick={onCancel} aria-label="Close" className="rounded-full p-1 text-[#7B6157] hover:bg-[#F5F2EB]"><X size={16} /></button></div>
        <p className="text-sm text-[#6D4C41]">A new draft is created with today's date, exactly as written: {rows.map((r) => lineLabel(r.l)).join(", ")}.</p>
        <p className="mt-2 text-xs text-[#7B6157]">Samples and Med/Ed discussed are not copied, because they belong to the visit. Review the draft, then sign it.</p>
        <div className="mt-5 flex justify-end gap-2"><Btn variant="ghost" onClick={onCancel}>Cancel</Btn><Btn data-testid="rx-duplicate-yes" disabled={busy} onClick={onConfirm}><Copy size={14} /> Yes, duplicate</Btn></div>
      </div>
    </div>
  );
}

/** Prescription Record Sheet: every medication ever prescribed for the client, with New, Open, Duplicate, Print scripts, and Print list. */
export function RxRecordSheet({ client, items, me, onNew, onOpen, onDuplicated, onReload }) {
  const { user } = useAuth();
  const [selected, setSelected] = useState(new Set());
  const [focus, setFocus] = useState(null);
  const [dup, setDup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [printing, setPrinting] = useState(false);
  const rows = useMemo(() => items.flatMap((rx) => rx.lines.map((l) => ({ key: `${rx.id}:${l.id}`, rx, l })))
    .sort((a, b) => dateOf(b.rx).localeCompare(dateOf(a.rx)) || (b.rx.created_at || "").localeCompare(a.rx.created_at || "")), [items]);
  const picked = rows.filter((r) => selected.has(r.key));
  const toggle = (k) => setSelected((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const focusRow = rows.find((r) => r.key === focus) || picked[0] || null;

  const printScripts = () => {
    if (!picked.length) return toast.error("Select one or more medications to print");
    const bad = picked.filter((r) => !printable(r));
    if (bad.length) return toast.error(`Only signed medications that are not voided or discontinued can be printed. Unselect ${bad.map((r) => r.l.medication).join(", ")}.`);
    setPrinting(true);
  };
  const doPrint = (o) => {
    setPrinting(false);
    const byRx = {};
    picked.forEach((r) => { (byRx[r.rx.id] = byRx[r.rx.id] || { rx: { ...r.rx, client: clientBlock(client) }, lines: [] }).lines.push(r.l); });
    const jobs = Object.values(byRx);
    printPrescriptions(jobs, { ...o, printedBy: `${user.first_name || ""} ${user.last_name || ""}`.trim() });
    Promise.all(jobs.map((j) => api.post(`/prescriptions/${j.rx.id}/printed`, { line_ids: j.lines.map((l) => l.id), layout: layoutLabel(o) })))
      .then(() => { toast.success(`Printed ${picked.length} medication${picked.length === 1 ? "" : "s"}`); setSelected(new Set()); onReload(); })
      .catch((e) => toast.error(apiError(e)));
  };
  const startDup = () => {
    if (!picked.length) return toast.error("Select one or more medications to duplicate");
    const bad = picked.filter((r) => !duplicable(r));
    if (bad.length) return toast.error(`Voided or discontinued medications cannot be duplicated. Unselect ${bad.map((r) => r.l.medication).join(", ")}.`);
    if (picked.length > 10) return toast.error("Duplicate at most 10 medications at a time");
    setDup(true);
  };
  const doDup = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/clients/${client.id}/prescriptions/duplicate`, { items: picked.map((x) => ({ rx_id: x.rx.id, line_id: x.l.id })) });
      toast.success(`Draft ${r.data.rx_number} created`); setDup(false); setSelected(new Set()); onDuplicated(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const tool = "flex items-center gap-1.5";
  return (
    <div className="space-y-3" data-testid="rx-record-sheet">
      <div className="flex flex-wrap items-center gap-2" data-testid="rx-sheet-toolbar">
        {me.is_prescriber && <Btn data-testid="rx-new-btn" onClick={onNew} className={tool}><Plus size={14} /> New</Btn>}
        <Btn variant="outline" data-testid="rx-sheet-open" disabled={!focusRow} onClick={() => onOpen(focusRow.rx.id)}><FolderOpen size={14} /> Open</Btn>
        {me.is_prescriber && <Btn variant="outline" data-testid="rx-sheet-duplicate" onClick={startDup}><Copy size={14} /> Duplicate</Btn>}
        <Btn variant="outline" data-testid="rx-sheet-print" onClick={printScripts}><Printer size={14} /> Print scripts</Btn>
        <Btn variant="outline" data-testid="rx-sheet-print-list" disabled={!rows.length} onClick={() => printRxList(picked.length ? picked : rows, clientBlock(client).name, `${user.first_name || ""} ${user.last_name || ""}`.trim())}><ListChecks size={14} /> Print list</Btn>
        <span className="ml-auto text-xs text-[#7B6157]" data-testid="rx-sheet-count">{picked.length ? `${picked.length} selected of ${rows.length}` : `${rows.length} medication${rows.length === 1 ? "" : "s"}`}</span>
      </div>
      {rows.length ? <RecordSheetTable rows={rows} selected={selected} focus={focusRow?.key} onToggle={toggle} onFocus={setFocus} onOpen={onOpen}
        onToggleAll={() => setSelected(picked.length === rows.length ? new Set() : new Set(rows.map((r) => r.key)))} /> : <Empty text="No prescriptions yet." />}
      <PharmacyStrip row={focusRow} />
      {dup && <DuplicateConfirm rows={picked} busy={busy} onCancel={() => setDup(false)} onConfirm={doDup} />}
      <RxPrintDialog open={printing} count={picked.length} onClose={() => setPrinting(false)} onPrint={doPrint} />
    </div>
  );
}
